/**
 * asaasPayout — o REPASSE ao prestador, do ponto de vista da tela. MÓDULO PURO.
 *
 * ─── A METADE QUE FALTAVA DA PROMESSA ───────────────────────────────────────
 *
 * O produto promete "você não faz nada": o cliente paga, o app dá baixa e o
 * dinheiro cai na conta do prestador. As duas primeiras partes já funcionavam e
 * a terceira era INVISÍVEL — nenhuma tela lia `asaas_transfers`. Então um
 * repasse que falha deixa o dinheiro parado no saldo da subconta com o
 * prestador achando que recebeu, porque a única coisa que ele viu foi o aviso
 * "pagamento recebido".
 *
 * Não é hipótese: o primeiro repasse real falhou com `A chave informada não foi
 * encontrada` e o dinheiro ficou retido (está no cabeçalho de
 * `asaas-payout-retry`). A função de retentativa já existia, deployada, e nada
 * no app a chamava.
 *
 * ─── POR QUE O ESTADO DO REPASSE NÃO É O ESTADO DA COBRANÇA ─────────────────
 *
 * São duas pernas independentes do mesmo dinheiro: `received` diz que o cliente
 * pagou; `done` diz que o prestador recebeu. Entre uma e outra dá para falhar,
 * e é exatamente aí que o silêncio custa caro. Por isso a tela precisa dos dois
 * e não pode derivar um do outro.
 *
 * Exceção que confirma a regra: `received_in_cash` é pagamento SEM repasse —
 * pago em espécie, fora do Asaas, não há saldo para transferir. Cobrar repasse
 * nesse caso mostraria um erro eterno numa venda que deu certo.
 */

/** Espelha o enum `public.asaas_transfer_status`. */
export type AsaasTransferStatus = 'requested' | 'authorized' | 'done' | 'failed' | 'cancelled';

export interface AsaasTransferLike {
  id?: string;
  status?: string | null;
  value?: number | string | null;
  last_error?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
}

/**
 * O que a tela diz sobre o dinheiro do prestador.
 *
 *  `none`      não se aplica (a venda não foi paga pelo Asaas, ou foi em espécie)
 *  `pending`   pedido/autorizado — a caminho
 *  `done`      caiu na conta
 *  `failed`    🔴 PAROU. Tem dinheiro retido e o prestador precisa agir.
 *  `missing`   o cliente pagou pelo Asaas e NÃO existe repasse. Também é uma
 *              pendência: o dinheiro está no saldo e ninguém pediu para sair.
 */
export type PayoutState = 'none' | 'pending' | 'done' | 'failed' | 'missing';

export interface PayoutView {
  state: PayoutState;
  /** Dá para acionar a retentativa agora? */
  canRetry: boolean;
  /** Motivo da falha, cru do provedor — só quando `failed`. */
  error: string | null;
}

const DONE = 'done';
const FAILED_STATES = new Set(['failed', 'cancelled']);
const PENDING_STATES = new Set(['requested', 'authorized']);

/**
 * O estado do repasse de UMA cobrança.
 *
 * `chargeStatus` é a primeira pergunta, e não a última: só `received` (dinheiro
 * no SALDO do Asaas) gera repasse. `confirmed` ainda não liquidou e
 * `received_in_cash` nunca vai liquidar — cobrar repasse nos dois mostraria uma
 * pendência que não existe.
 */
export function resolvePayoutView(input: {
  chargeStatus?: string | null;
  /**
   * `undefined` = ainda NÃO SABEMOS (consulta carregando, offline, erro);
   * `null` = sabemos que não existe repasse.
   *
   * A distinção não é preciosismo: `null` vira o alerta "o repasse ainda não
   * saiu", e mostrá-lo durante o carregamento é alarme falso — o prestador leria
   * "tem dinheiro parado" toda vez que abrisse a tela. Mesmo trio de estados de
   * `resolvePixQrMode` e do financeiro na trilha (#121).
   */
  transfer?: AsaasTransferLike | null;
}): PayoutView {
  const inert: PayoutView = { state: 'none', canRetry: false, error: null };

  // Só o que está no saldo do Asaas pode ser repassado. Espelha `canPayout` do
  // servidor (`_shared/asaas/status.ts`) — e a divergência com `isPaid` é o
  // ponto: `received_in_cash` é pagamento e NÃO é repasse.
  if (input.chargeStatus !== 'received') return inert;

  // Não sabemos ainda: nada de alarme.
  if (input.transfer === undefined) return inert;

  const transfer = input.transfer;
  // Pagou e não há repasse nenhum: o dinheiro está no saldo e ninguém pediu
  // para sair. É pendência, não "tudo certo" — tratá-la como `none` é o mesmo
  // silêncio que esta feature veio corrigir.
  if (!transfer || typeof transfer.status !== 'string') {
    return { state: 'missing', canRetry: true, error: null };
  }

  if (transfer.status === DONE) return { state: 'done', canRetry: false, error: null };

  if (FAILED_STATES.has(transfer.status)) {
    return {
      state: 'failed',
      canRetry: true,
      error: typeof transfer.last_error === 'string' && transfer.last_error.trim()
        ? transfer.last_error.trim()
        : null,
    };
  }

  if (PENDING_STATES.has(transfer.status)) {
    return { state: 'pending', canRetry: false, error: null };
  }

  // Status novo do provedor: NÃO vira "concluído" por omissão — anunciar que o
  // dinheiro chegou sem saber é a mentira mais cara possível aqui. Cai em
  // "a caminho", que é verdade em qualquer estado intermediário.
  return { state: 'pending', canRetry: false, error: null };
}

/** Precisa de atenção do prestador AGORA (tem dinheiro parado). */
export function payoutNeedsAttention(view: PayoutView): boolean {
  return view.state === 'failed' || view.state === 'missing';
}

/**
 * O repasse mais recente entre os que existirem para a cobrança.
 *
 * O índice único do banco garante NO MÁXIMO um repasse vivo por cobrança, mas
 * `failed` está fora dele de propósito (é o que permite a retentativa) — então
 * uma cobrança pode acumular várias linhas falhas mais a viva. A mais recente é
 * a que descreve a situação.
 */
export function pickCurrentTransfer<T extends AsaasTransferLike>(
  transfers: readonly T[] | null | undefined,
): T | null {
  if (!Array.isArray(transfers) || transfers.length === 0) return null;
  // Uma linha viva (não-falha) descreve melhor a situação do que uma falha
  // anterior: é a tentativa que ainda pode dar certo.
  const alive = transfers.filter((t) => typeof t.status === 'string' && !FAILED_STATES.has(t.status));
  const pool = alive.length > 0 ? alive : transfers;
  return [...pool].sort(
    (a, b) => (Date.parse(b.created_at ?? '') || 0) - (Date.parse(a.created_at ?? '') || 0),
  )[0];
}

/**
 * Cadência para acompanhar o repasse, e o teto do caso "ainda não existe linha".
 *
 * A janela real é curta — medida no E2E de sandbox, o repasse foi pedido,
 * autorizado e concluído em **14 segundos** —, então 5s acompanha sem exagero.
 *
 * O TETO existe por causa de uma corrida estreita e real: o webhook atualiza a
 * cobrança para `received` ANTES de pedir o repasse, então há uma fração de
 * segundo em que a cobrança já está paga e a linha do repasse ainda não nasceu.
 * Uma consulta que caia exatamente aí veria `missing` ("o repasse ainda não
 * saiu") e, sem repergunta, o card ficaria nesse texto até a tela remontar.
 * Depois do teto paramos: `missing` é estado ACIONÁVEL (tem botão), não uma
 * espera — e perguntar para sempre não o tornaria mais verdadeiro.
 */
export const PAYOUT_POLL_MS = 5_000;
export const PAYOUT_POLL_MAX_EMPTY_TICKS = 6;

/**
 * Vale reperguntar sobre o repasse?
 *
 * Chamado só quando a consulta está habilitada — ou seja, com a cobrança já em
 * `received`. Termina sempre: `done`/`failed`/`cancelled` são finais, e a
 * ausência de linha é limitada pelo teto acima.
 *
 * `failed` NÃO continua perguntando de propósito: quem destrava é o prestador
 * pelo botão de retentativa, e a retentativa invalida a consulta — a linha nova
 * nasce `requested` e volta a ser acompanhada por este mesmo caminho.
 */
export function shouldPollPayout(
  transfers: readonly AsaasTransferLike[] | null | undefined,
  fetchCount: number,
): boolean {
  const transfer = pickCurrentTransfer(transfers ?? []);
  if (!transfer) return fetchCount < PAYOUT_POLL_MAX_EMPTY_TICKS;
  return typeof transfer.status === 'string' && PENDING_STATES.has(transfer.status);
}

/** O que a tela escreve em cada estado. Texto no módulo puro, como os hints. */
export const PAYOUT_STATE_LABELS: Record<PayoutState, string> = {
  none: '',
  pending: 'Enviando para a sua conta…',
  done: 'O valor já caiu na sua conta.',
  failed: 'O repasse falhou e o valor está retido. Confira sua chave Pix e tente de novo.',
  missing: 'O pagamento entrou, mas o repasse ainda não saiu. Você pode pedir agora.',
};

/**
 * Os motivos que `asaas-payout-retry` devolve, em português e ACIONÁVEIS.
 *
 * O corpo carrega o veredito e o HTTP nem sempre (#9): a função responde 409 com
 * `{ requested: false, reason }` — então quem decide se deu certo é `requested`,
 * nunca o status da resposta sozinho.
 */
export function payoutRetryMessage(reason: string | null | undefined): string {
  switch (reason) {
    case 'requested':
      return 'Repasse solicitado. O valor deve cair na sua conta em instantes.';
    case 'already_requested':
      return 'Já existe um repasse em andamento para este pagamento.';
    case 'no_payout_key':
      return 'Cadastre a chave Pix que vai receber o repasse e tente de novo.';
    case 'pix_key_type_unknown':
      return 'Não reconhecemos o tipo da sua chave Pix. Revise o cadastro e tente de novo.';
    case 'not_settled':
      return 'O pagamento ainda não foi liquidado. Tente de novo em alguns minutos.';
    case 'net_value_unknown':
      return 'Ainda não sabemos o valor líquido deste pagamento. Tente de novo em alguns minutos.';
    case 'not_payable':
      return 'Este pagamento não gera repasse.';
    case 'forbidden':
      return 'Você não tem permissão para pedir o repasse desta empresa.';
    case 'charge_not_found':
      return 'Não encontramos este pagamento. Recarregue a tela e tente de novo.';
    default:
      return 'Não foi possível pedir o repasse agora. Tente de novo em alguns minutos.';
  }
}
